import Phaser from 'phaser';
import { PLANTS, type PlantSpecies } from '../data/plants';

interface BattlePlant {
  species: PlantSpecies;
  hp: number;
  maxHp: number;
  sprite?: Phaser.GameObjects.Sprite;
  hpBar?: Phaser.GameObjects.Graphics;
  hpText?: Phaser.GameObjects.Text;
  isDefending: boolean;
}

export class BattleScene extends Phaser.Scene {
  private playerPlant!: BattlePlant;
  private enemyPlant!: BattlePlant;
  private isPlayerTurn: boolean = true;
  private battleLog!: Phaser.GameObjects.Text;
  private turnIndicator!: Phaser.GameObjects.Text;
  private actionButtons: Phaser.GameObjects.Container[] = [];
  private isNight: boolean = false;
  private returnScene: string = 'GameScene';
  private turnCount: number = 0;
  private battleEnded: boolean = false;

  constructor() {
    super({ key: 'BattleScene' });
  }

  init(data: { playerPlantId: string; enemyPlantId: string; isNight?: boolean; returnScene?: string }): void {
    const playerSpecies = PLANTS[data.playerPlantId];
    const enemySpecies = PLANTS[data.enemyPlantId];

    this.isNight = data.isNight ?? false;
    this.returnScene = data.returnScene ?? 'GameScene';
    this.turnCount = 0;
    this.isPlayerTurn = true;
    this.battleEnded = false;
    this.actionButtons = [];

    const calcHp = (species: PlantSpecies) => 60 + species.defensePower * 2 + species.attackPower;

    this.playerPlant = {
      species: playerSpecies,
      hp: calcHp(playerSpecies),
      maxHp: calcHp(playerSpecies),
      isDefending: false,
    };

    this.enemyPlant = {
      species: enemySpecies,
      hp: calcHp(enemySpecies),
      maxHp: calcHp(enemySpecies),
      isDefending: false,
    };
  }

  create(): void {
    const width = this.cameras.main.width;
    const height = this.cameras.main.height;

    // Battle background
    this.createBattleBackground(width, height);

    // Plant sprites
    this.playerPlant.sprite = this.add.sprite(width * 0.25, height * 0.55, 'plants', this.playerPlant.species.spriteRow * 4 + 3);
    this.playerPlant.sprite.setScale(6);

    this.enemyPlant.sprite = this.add.sprite(width * 0.75, height * 0.35, 'plants', this.enemyPlant.species.spriteRow * 4 + 3);
    this.enemyPlant.sprite.setScale(6);
    this.enemyPlant.sprite.setFlipX(true);

    // Idle sway animation
    this.tweens.add({ targets: this.playerPlant.sprite, angle: 2, duration: 2000, yoyo: true, repeat: -1, ease: 'Sine.easeInOut' });
    this.tweens.add({ targets: this.enemyPlant.sprite, angle: -2, duration: 1800, yoyo: true, repeat: -1, ease: 'Sine.easeInOut' });

    // HP bars and text
    this.createHpBars(width, height);

    // Names
    this.add.text(width * 0.25, height * 0.75, this.playerPlant.species.name, {
      fontFamily: 'monospace', fontSize: '14px', color: '#6aaa3a', stroke: '#000000', strokeThickness: 2,
    }).setOrigin(0.5, 0);

    this.add.text(width * 0.75, height * 0.15, this.enemyPlant.species.name, {
      fontFamily: 'monospace', fontSize: '14px', color: '#d4573b', stroke: '#000000', strokeThickness: 2,
    }).setOrigin(0.5, 0);

    // Type badges
    const playerType = this.playerPlant.species.type.toUpperCase();
    const enemyType = this.enemyPlant.species.type.toUpperCase();
    this.add.text(width * 0.25, height * 0.78, `[${playerType}]`, {
      fontFamily: 'monospace', fontSize: '9px', color: '#999999',
    }).setOrigin(0.5, 0);
    this.add.text(width * 0.75, height * 0.18, `[${enemyType}]`, {
      fontFamily: 'monospace', fontSize: '9px', color: '#999999',
    }).setOrigin(0.5, 0);

    // Day/Night indicator
    const timeLabel = this.isNight ? 'Night Battle' : 'Day Battle';
    const timeColor = this.isNight ? '#8b68c4' : '#e8a038';
    this.add.text(width / 2, 10, timeLabel, {
      fontFamily: 'monospace', fontSize: '12px', color: timeColor, stroke: '#000000', strokeThickness: 2,
    }).setOrigin(0.5, 0);

    // "VS" text
    const vs = this.add.text(width / 2, height * 0.45, 'VS', {
      fontFamily: 'monospace', fontSize: '24px', color: '#ffffff', stroke: '#000000', strokeThickness: 4,
    });
    vs.setOrigin(0.5, 0.5);
    vs.setAlpha(0);
    this.tweens.add({ targets: vs, alpha: 0.3, duration: 800, delay: 500 });

    // Turn indicator (above the 2x2 button grid)
    this.turnIndicator = this.add.text(width / 2, height - 110, '>> Your Turn <<', {
      fontFamily: 'monospace', fontSize: '11px', color: '#6aaa3a', stroke: '#000000', strokeThickness: 2,
    });
    this.turnIndicator.setOrigin(0.5, 0.5);

    // Battle log
    this.battleLog = this.add.text(width / 2, height - 100, 'Battle Start! Choose your action.', {
      fontFamily: 'monospace', fontSize: '11px', color: '#ffffff',
      backgroundColor: '#000000aa', padding: { x: 8, y: 4 }, wordWrap: { width: 400 },
    });
    this.battleLog.setOrigin(0.5, 0);

    // Action buttons
    this.createActionButtons(width, height);

    // Apply day/night bonus
    this.applyTimeBonus();

    // Entry animations
    this.playerPlant.sprite.setX(-60);
    this.enemyPlant.sprite.setX(width + 60);
    this.tweens.add({ targets: this.playerPlant.sprite, x: width * 0.25, duration: 600, ease: 'Back.easeOut' });
    this.tweens.add({ targets: this.enemyPlant.sprite, x: width * 0.75, duration: 600, ease: 'Back.easeOut' });

    // Screen flash
    this.cameras.main.flash(300, 255, 255, 255);
  }

  private createBattleBackground(width: number, height: number): void {
    const bg = this.add.graphics();

    if (this.isNight) {
      for (let y = 0; y < height; y++) {
        const t = y / height;
        bg.fillStyle(Phaser.Display.Color.GetColor(
          Math.floor(20 + t * 15), Math.floor(10 + t * 30), Math.floor(50 + t * 40)
        ));
        bg.fillRect(0, y, width, 1);
      }
      // Stars
      for (let i = 0; i < 20; i++) {
        const sx = Phaser.Math.Between(0, width);
        const sy = Phaser.Math.Between(0, Math.floor(height * 0.6));
        const star = this.add.circle(sx, sy, 1, 0xffffff, Phaser.Math.FloatBetween(0.3, 0.7));
        this.tweens.add({ targets: star, alpha: 0.1, duration: Phaser.Math.Between(1000, 3000), yoyo: true, repeat: -1 });
      }
    } else {
      for (let y = 0; y < height; y++) {
        const t = y / height;
        bg.fillStyle(Phaser.Display.Color.GetColor(
          Math.floor(100 + t * 30), Math.floor(180 + t * -40), Math.floor(255 + t * -100)
        ));
        bg.fillRect(0, y, width, 1);
      }
    }

    // Ground with grass tufts
    bg.fillStyle(this.isNight ? 0x1a3a1a : 0x4a7c4b);
    bg.fillRect(0, height * 0.65, width, height * 0.35);
    bg.fillStyle(this.isNight ? 0x2a4a2a : 0x5a8c5b);
    bg.fillRect(0, height * 0.65, width, 3);
  }

  private createHpBars(width: number, height: number): void {
    this.playerPlant.hpBar = this.add.graphics();
    this.drawHpBar(this.playerPlant.hpBar, width * 0.25 - 50, height * 0.72, this.playerPlant);
    this.playerPlant.hpText = this.add.text(width * 0.25, height * 0.72 + 12, '', {
      fontFamily: 'monospace', fontSize: '10px', color: '#ffffff',
    }).setOrigin(0.5, 0);
    this.updateHpText(this.playerPlant);

    this.enemyPlant.hpBar = this.add.graphics();
    this.drawHpBar(this.enemyPlant.hpBar, width * 0.75 - 50, height * 0.10, this.enemyPlant);
    this.enemyPlant.hpText = this.add.text(width * 0.75, height * 0.10 + 12, '', {
      fontFamily: 'monospace', fontSize: '10px', color: '#ffffff',
    }).setOrigin(0.5, 0);
    this.updateHpText(this.enemyPlant);
  }

  private updateHpText(plant: BattlePlant): void {
    if (plant.hpText) {
      plant.hpText.setText(`${plant.hp}/${plant.maxHp}`);
    }
  }

  private drawHpBar(graphics: Phaser.GameObjects.Graphics, x: number, y: number, plant: BattlePlant): void {
    graphics.clear();
    const w = 100;
    const h = 10;

    // Background
    graphics.fillStyle(0x222222);
    graphics.fillRect(x, y, w, h);

    // HP fill
    const ratio = plant.hp / plant.maxHp;
    const color = ratio > 0.5 ? 0x6aaa3a : ratio > 0.25 ? 0xe8a038 : 0xd4573b;
    graphics.fillStyle(color);
    graphics.fillRect(x + 1, y + 1, (w - 2) * ratio, h - 2);

    // Border
    graphics.lineStyle(1, 0xffffff, 0.6);
    graphics.strokeRect(x, y, w, h);
  }

  private createActionButtons(width: number, height: number): void {
    const actions = [
      { name: 'Attack', color: 0xd4573b },
      { name: 'Special', color: 0x8b68c4 },
      { name: 'Defend', color: 0x4a7cbb },
      { name: 'Flee', color: 0x666666 },
    ];

    // 2x2 grid layout for better mobile touch targets
    const btnWidth = Math.min(140, (width - 40) / 2);
    const btnHeight = 36;
    const gapX = 8;
    const gapY = 6;
    const gridW = btnWidth * 2 + gapX;
    const baseX = width / 2;
    const baseY = height - btnHeight - gapY / 2 - 10;

    actions.forEach((action, i) => {
      const col = i % 2;
      const row = Math.floor(i / 2);
      const x = baseX - gridW / 2 + col * (btnWidth + gapX) + btnWidth / 2;
      const y = baseY - row * (btnHeight + gapY);
      const container = this.add.container(x, y);

      const bg = this.add.graphics();
      bg.fillStyle(action.color, 0.85);
      bg.fillRoundedRect(-btnWidth / 2, -btnHeight / 2, btnWidth, btnHeight, 6);
      bg.lineStyle(1, 0xffffff, 0.3);
      bg.strokeRoundedRect(-btnWidth / 2, -btnHeight / 2, btnWidth, btnHeight, 6);
      container.add(bg);

      const label = this.add.text(0, 0, action.name, {
        fontFamily: 'monospace', fontSize: '13px', color: '#ffffff', fontStyle: 'bold',
      });
      label.setOrigin(0.5, 0.5);
      container.add(label);

      // Large hit zone for comfortable touch
      const hitArea = this.add.zone(0, 0, btnWidth + 4, btnHeight + 4).setInteractive({ useHandCursor: true });
      container.add(hitArea);

      hitArea.on('pointerover', () => {
        bg.clear();
        bg.fillStyle(action.color, 1);
        bg.fillRoundedRect(-btnWidth / 2, -btnHeight / 2, btnWidth, btnHeight, 6);
        bg.lineStyle(2, 0xffffff, 0.6);
        bg.strokeRoundedRect(-btnWidth / 2, -btnHeight / 2, btnWidth, btnHeight, 6);
        container.setScale(1.03);
      });

      hitArea.on('pointerout', () => {
        bg.clear();
        bg.fillStyle(action.color, 0.85);
        bg.fillRoundedRect(-btnWidth / 2, -btnHeight / 2, btnWidth, btnHeight, 6);
        bg.lineStyle(1, 0xffffff, 0.3);
        bg.strokeRoundedRect(-btnWidth / 2, -btnHeight / 2, btnWidth, btnHeight, 6);
        container.setScale(1);
      });

      hitArea.on('pointerdown', () => {
        if (this.isPlayerTurn) {
          this.performAction(action.name.toLowerCase());
        }
      });

      this.actionButtons.push(container);
    });
  }

  private applyTimeBonus(): void {
    const applyBonus = (plant: BattlePlant) => {
      if (this.isNight && plant.species.type === 'night') {
        plant.maxHp = Math.floor(plant.maxHp * 1.3);
        plant.hp = plant.maxHp;
      } else if (!this.isNight && plant.species.type === 'day') {
        plant.maxHp = Math.floor(plant.maxHp * 1.3);
        plant.hp = plant.maxHp;
      } else if (
        (this.isNight && plant.species.type === 'day') ||
        (!this.isNight && plant.species.type === 'night')
      ) {
        plant.maxHp = Math.floor(plant.maxHp * 0.7);
        plant.hp = plant.maxHp;
      }
    };
    applyBonus(this.playerPlant);
    applyBonus(this.enemyPlant);
  }

  private setButtonsEnabled(enabled: boolean): void {
    this.actionButtons.forEach(btn => {
      btn.setAlpha(enabled ? 1 : 0.4);
    });
    this.turnIndicator.setText(enabled ? '>> Your Turn <<' : '... Enemy Turn ...');
    this.turnIndicator.setColor(enabled ? '#6aaa3a' : '#d4573b');
  }

  private performAction(action: string): void {
    if (!this.isPlayerTurn) return;
    this.isPlayerTurn = false;
    this.turnCount++;
    this.setButtonsEnabled(false);
    this.playerPlant.isDefending = false;

    let damage = 0;
    let logMessage = '';

    switch (action) {
      case 'attack': {
        damage = this.calculateDamage(this.playerPlant, this.enemyPlant, false);
        this.enemyPlant.hp = Math.max(0, this.enemyPlant.hp - damage);
        logMessage = `${this.playerPlant.species.name} attacks for ${damage} damage!`;
        this.playAttackAnimation(this.playerPlant.sprite!, this.enemyPlant.sprite!, damage);
        break;
      }
      case 'special': {
        damage = this.calculateDamage(this.playerPlant, this.enemyPlant, true);
        this.enemyPlant.hp = Math.max(0, this.enemyPlant.hp - damage);
        logMessage = `${this.playerPlant.species.name} uses ${this.playerPlant.species.specialAbility} for ${damage} damage!`;
        this.playSpecialAnimation(this.playerPlant.sprite!, this.enemyPlant.sprite!, damage);
        break;
      }
      case 'defend': {
        this.playerPlant.isDefending = true;
        logMessage = `${this.playerPlant.species.name} braces for impact! (50% damage reduction)`;
        // Shield visual
        this.playDefendAnimation(this.playerPlant.sprite!);
        break;
      }
      case 'flee': {
        this.battleLog.setText('Got away safely!');
        this.cameras.main.fade(800, 0, 0, 0);
        this.time.delayedCall(800, () => this.endBattle(false));
        return;
      }
    }

    this.battleLog.setText(logMessage);
    this.updateHpBars();

    if (this.enemyPlant.hp <= 0) {
      this.time.delayedCall(800, () => {
        this.playDefeatAnimation(this.enemyPlant.sprite!);
        this.battleLog.setText(`${this.enemyPlant.species.name} was defeated! You win!`);
        this.time.delayedCall(1500, () => this.endBattle(true));
      });
      return;
    }

    this.time.delayedCall(1200, () => this.enemyTurn());
  }

  private enemyTurn(): void {
    this.enemyPlant.isDefending = false;

    // Smarter AI: use special more often when low HP, defend when critical
    const hpRatio = this.enemyPlant.hp / this.enemyPlant.maxHp;
    let action: 'attack' | 'special' | 'defend';
    const roll = Math.random();

    if (hpRatio < 0.2 && roll < 0.4) {
      action = 'defend';
    } else if (hpRatio < 0.5 && roll < 0.5) {
      action = 'special';
    } else if (roll < 0.35) {
      action = 'special';
    } else {
      action = 'attack';
    }

    if (action === 'defend') {
      this.enemyPlant.isDefending = true;
      this.battleLog.setText(`${this.enemyPlant.species.name} braces for impact!`);
      this.playDefendAnimation(this.enemyPlant.sprite!);
    } else {
      const isSpecial = action === 'special';
      let damage = this.calculateDamage(this.enemyPlant, this.playerPlant, isSpecial);

      // Apply defend reduction
      if (this.playerPlant.isDefending) {
        damage = Math.floor(damage * 0.5);
      }

      this.playerPlant.hp = Math.max(0, this.playerPlant.hp - damage);

      const abilityName = isSpecial ? this.enemyPlant.species.specialAbility : 'attacks';
      const defendNote = this.playerPlant.isDefending ? ' (defended!)' : '';
      this.battleLog.setText(`${this.enemyPlant.species.name} ${abilityName} for ${damage} damage!${defendNote}`);

      if (isSpecial) {
        this.playSpecialAnimation(this.enemyPlant.sprite!, this.playerPlant.sprite!, damage);
      } else {
        this.playAttackAnimation(this.enemyPlant.sprite!, this.playerPlant.sprite!, damage);
      }
    }

    this.updateHpBars();

    if (this.playerPlant.hp <= 0) {
      this.time.delayedCall(800, () => {
        this.playDefeatAnimation(this.playerPlant.sprite!);
        this.battleLog.setText(`${this.playerPlant.species.name} was defeated! You lose...`);
        this.time.delayedCall(1500, () => this.endBattle(false));
      });
      return;
    }

    this.time.delayedCall(1000, () => {
      this.isPlayerTurn = true;
      this.playerPlant.isDefending = false;
      this.setButtonsEnabled(true);
    });
  }

  private calculateDamage(attacker: BattlePlant, defender: BattlePlant, isSpecial: boolean): number {
    const basePower = isSpecial ? attacker.species.attackPower * 1.8 : attacker.species.attackPower;
    let defense = defender.species.defensePower * 0.3;
    if (defender.isDefending) defense *= 2;
    const variance = 0.85 + Math.random() * 0.3;
    return Math.max(1, Math.floor((basePower - defense) * variance));
  }

  // --- Battle Animations ---

  private playAttackAnimation(attacker: Phaser.GameObjects.Sprite, target: Phaser.GameObjects.Sprite, damage: number): void {
    // Lunge forward
    const dx = target.x - attacker.x;
    this.tweens.add({
      targets: attacker,
      x: attacker.x + dx * 0.3,
      duration: 150,
      yoyo: true,
      ease: 'Power2',
    });

    // Flash and shake target
    this.time.delayedCall(150, () => {
      target.setTint(0xff4444);
      this.cameras.main.shake(100, 0.005);
      this.showDamageNumber(target.x, target.y - 30, damage);
      this.time.delayedCall(200, () => target.clearTint());
    });
  }

  private playSpecialAnimation(attacker: Phaser.GameObjects.Sprite, target: Phaser.GameObjects.Sprite, damage: number): void {
    // Glow + particles
    attacker.setTint(0xffff88);
    this.time.delayedCall(300, () => attacker.clearTint());

    // Projectile particles from attacker to target
    for (let i = 0; i < 5; i++) {
      const p = this.add.circle(
        attacker.x + Phaser.Math.Between(-10, 10),
        attacker.y + Phaser.Math.Between(-10, 10),
        3, this.isNight ? 0x8b68c4 : 0xe8a038, 0.9
      );
      this.tweens.add({
        targets: p,
        x: target.x + Phaser.Math.Between(-15, 15),
        y: target.y + Phaser.Math.Between(-15, 15),
        alpha: 0,
        duration: 400,
        delay: i * 60,
        onComplete: () => p.destroy(),
      });
    }

    this.time.delayedCall(350, () => {
      target.setTint(0xff4444);
      this.cameras.main.shake(150, 0.008);
      this.showDamageNumber(target.x, target.y - 30, damage, true);
      this.time.delayedCall(250, () => target.clearTint());
    });
  }

  private playDefendAnimation(sprite: Phaser.GameObjects.Sprite): void {
    // Blue shield ring
    const shield = this.add.circle(sprite.x, sprite.y, 30, 0x4a7cbb, 0);
    shield.setStrokeStyle(3, 0x4a7cbb, 0.8);
    this.tweens.add({
      targets: shield,
      alpha: 0.3,
      scaleX: 1.2,
      scaleY: 1.2,
      duration: 400,
      yoyo: true,
      onComplete: () => shield.destroy(),
    });
  }

  private playDefeatAnimation(sprite: Phaser.GameObjects.Sprite): void {
    this.tweens.add({
      targets: sprite,
      alpha: 0,
      scaleX: 0.3,
      scaleY: 0.3,
      angle: 45,
      y: sprite.y + 30,
      duration: 600,
      ease: 'Power2',
    });
    this.cameras.main.flash(200, 255, 255, 255);
  }

  private showDamageNumber(x: number, y: number, damage: number, isSpecial: boolean = false): void {
    const color = isSpecial ? '#ffaa00' : '#ff4444';
    const text = this.add.text(x, y, `-${damage}`, {
      fontFamily: 'monospace',
      fontSize: isSpecial ? '18px' : '14px',
      color,
      stroke: '#000000',
      strokeThickness: 3,
      fontStyle: 'bold',
    });
    text.setOrigin(0.5, 0.5);

    this.tweens.add({
      targets: text,
      y: y - 30,
      alpha: 0,
      duration: 800,
      ease: 'Power2',
      onComplete: () => text.destroy(),
    });
  }

  private updateHpBars(): void {
    const width = this.cameras.main.width;
    const height = this.cameras.main.height;
    this.drawHpBar(this.playerPlant.hpBar!, width * 0.25 - 50, height * 0.72, this.playerPlant);
    this.drawHpBar(this.enemyPlant.hpBar!, width * 0.75 - 50, height * 0.10, this.enemyPlant);
    this.updateHpText(this.playerPlant);
    this.updateHpText(this.enemyPlant);
  }

  shutdown(): void {
    // Stop all tweens and timers to prevent accumulation across battles
    this.tweens.killAll();
    this.time.removeAllEvents();

    // Remove all input listeners from action button hit zones
    this.actionButtons.forEach(btn => {
      btn.iterate((child: Phaser.GameObjects.GameObject) => {
        if (child instanceof Phaser.GameObjects.Zone) {
          child.removeAllListeners();
          child.disableInteractive();
        }
      });
    });
    this.actionButtons = [];

    // Remove scene-level input listeners
    this.input.removeAllListeners();
  }

  private endBattle(won: boolean): void {
    if (this.battleEnded) return;
    this.battleEnded = true;

    // Disable all input immediately to prevent double-actions
    this.isPlayerTurn = false;
    this.setButtonsEnabled(false);

    this.cameras.main.fade(500, 0, 0, 0, false, (_cam: Phaser.Cameras.Scene2D.Camera, progress: number) => {
      if (progress >= 1) {
        this.scene.stop('BattleScene');
        this.scene.resume(this.returnScene);
        this.scene.get(this.returnScene)?.events.emit('battle-result', { won });
      }
    });
  }
}
